import { useEffect, useState, type ReactNode } from "react";
import { MessageCircle, RadioTower, X } from "lucide-react";
import {
  RiskFlowNotificationCard,
  type Notification as RiskFlowNotification,
} from "./components/NotificationToast";
import { ComposerConnectorMark } from "./components/chat/composer/composerConnectors";
import { NothingFuse } from "./components/shared/NothingFuse";
import type {
  ConnectorIconName,
  ResponseScenario,
} from "./voice-cao-dock-data";

export function ConnectorCallList({
  response,
}: {
  response: ResponseScenario;
}) {
  return (
    <div className="voice-cao-tool-list" aria-label="Connector calls">
      {response.tools.map((tool) => (
        <div className="voice-cao-tool-call" key={tool.name}>
          <ConnectorIcon name={tool.icon} />
          <div>
            <b>{tool.name}</b>
            <span>{tool.action}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function MiniResponse({
  response,
  isTextReplyMode,
  onOpenSideChat,
}: {
  response: ResponseScenario;
  isTextReplyMode: boolean;
  onOpenSideChat: () => void;
}) {
  const responseKey = responseDismissKey(response);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const isResultDismissed = dismissedKey === responseKey;

  useEffect(() => {
    setDismissedKey(null);
  }, [responseKey]);

  return (
    <div className="voice-cao-response-mini">
      {!isResultDismissed && response.kind === "trade" && (
        <TradeIdeaMini
          response={response}
          onDismiss={() => setDismissedKey(responseKey)}
        />
      )}
      {!isResultDismissed && response.kind === "arbitrum" && (
        <LinkCtaMini
          response={response}
          onDismiss={() => setDismissedKey(responseKey)}
        />
      )}
      {!isResultDismissed && response.kind === "riskflow" && (
        <RiskFlowMini
          response={response}
          onDismiss={() => setDismissedKey(responseKey)}
        />
      )}
      {!isResultDismissed && response.kind === "open-chat" && (
        <OpenChatMini
          response={response}
          onDismiss={() => setDismissedKey(responseKey)}
          onOpenSideChat={onOpenSideChat}
        />
      )}
      {isTextReplyMode && <TextReplyRows response={response} />}
    </div>
  );
}

export function RiskFlowFloatCard({
  response,
}: {
  response: ResponseScenario;
}) {
  if (response.kind !== "riskflow" || !response.riskflowHeadline) return null;

  return (
    <aside className="voice-cao-risk-card-shell" aria-label="RiskFlow headline">
      <RiskFlowNotificationCard
        notification={riskFlowNotification}
        onDismiss={() => undefined}
        autoDismissMs={0}
      />
    </aside>
  );
}

function TradeIdeaMini({
  response,
  onDismiss,
}: {
  response: ResponseScenario;
  onDismiss: () => void;
}) {
  if (!response.tradeIdea) return null;

  return (
    <ResponseBlock onDismiss={onDismiss}>
      <b>{response.tradeIdea.label}</b>
      <dl className="voice-cao-trade-levels">
        <div>
          <dt>Entry</dt>
          <dd>{response.tradeIdea.entry}</dd>
        </div>
        <div>
          <dt>SL</dt>
          <dd>{response.tradeIdea.stopLoss}</dd>
        </div>
        <div>
          <dt>TP</dt>
          <dd>{response.tradeIdea.takeProfit}</dd>
        </div>
      </dl>
      <CtaRow>
        <MiniCta
          href={response.tradeIdea.href}
          label={response.tradeIdea.cta}
        />
      </CtaRow>
      <TradeConfidence score={response.tradeIdea.confidenceScore} />
    </ResponseBlock>
  );
}

function LinkCtaMini({
  response,
  onDismiss,
}: {
  response: ResponseScenario;
  onDismiss: () => void;
}) {
  if (!response.linkCta) return null;

  return (
    <ResponseBlock onDismiss={onDismiss}>
      <b>{response.linkCta.label}</b>
      {response.linkCta.detail ? <span>{response.linkCta.detail}</span> : null}
      <CtaRow>
        {response.linkCta.secondaryCta && response.linkCta.secondaryHref ? (
          <MiniCta
            href={response.linkCta.secondaryHref}
            label={response.linkCta.secondaryCta}
            variant="secondary"
          />
        ) : null}
        <MiniCta href={response.linkCta.href} label={response.linkCta.cta} />
      </CtaRow>
    </ResponseBlock>
  );
}

function RiskFlowMini({
  response,
  onDismiss,
}: {
  response: ResponseScenario;
  onDismiss: () => void;
}) {
  if (!response.riskflowHeadline) return null;

  return (
    <ResponseBlock onDismiss={onDismiss}>
      <b>{response.riskflowHeadline.label}</b>
      <span>{response.riskflowHeadline.headline}</span>
      <CtaRow>
        <MiniCta href={response.riskflowHeadline.href} label="Open" />
      </CtaRow>
    </ResponseBlock>
  );
}

function OpenChatMini({
  response,
  onDismiss,
  onOpenSideChat,
}: {
  response: ResponseScenario;
  onDismiss: () => void;
  onOpenSideChat: () => void;
}) {
  if (!response.openChat) return null;

  return (
    <ResponseBlock onDismiss={onDismiss}>
      {response.openChat.label ? <b>{response.openChat.label}</b> : null}
      <span>{response.openChat.detail}</span>
      <CtaRow>
        <MiniCta
          href={response.openChat.href}
          label="Open Chat"
          onActivate={onOpenSideChat}
        />
      </CtaRow>
    </ResponseBlock>
  );
}

function ResponseBlock({
  children,
  onDismiss,
}: {
  children: ReactNode;
  onDismiss: () => void;
}) {
  return (
    <div className="voice-cao-response-block">
      <button
        type="button"
        className="voice-cao-response-dismiss"
        aria-label="Dismiss result"
        title="Dismiss result"
        onClick={onDismiss}
      >
        <X aria-hidden="true" />
      </button>
      {children}
    </div>
  );
}

function responseDismissKey(response: ResponseScenario): string {
  return `${response.kind}:${response.phrase}:${response.stream}`;
}

function TextReplyRows({ response }: { response: ResponseScenario }) {
  return (
    <ol className="voice-cao-text-reply" aria-label="Text reply fallback">
      {response.textRows.slice(0, 6).map((row) => (
        <li key={row}>{row}</li>
      ))}
    </ol>
  );
}

function TradeConfidence({ score }: { score: number }) {
  return (
    <div className="voice-cao-trade-confidence">
      <div className="voice-cao-trade-confidence-label">
        <span>CAO Confidence</span>
        <b>{score.toFixed(1)}</b>
      </div>
      <NothingFuse
        value={score / 10}
        score={score}
        thickness={3}
        segments={10}
        animateIn
      />
    </div>
  );
}

function CtaRow({ children }: { children: ReactNode }) {
  return <div className="voice-cao-cta-row">{children}</div>;
}

function MiniCta({
  href,
  label,
  variant = "primary",
  onActivate,
}: {
  href: string;
  label: string;
  variant?: "primary" | "secondary";
  onActivate?: () => void;
}) {
  return (
    <a
      className={`voice-cao-mini-cta fintheon-toast-action-button fintheon-toast-action-button--${variant}${
        variant === "secondary" ? " fintheon-secondary-action" : ""
      }`}
      href={href}
      onClick={(event) => {
        if (!onActivate) return;
        event.preventDefault();
        onActivate();
      }}
    >
      {label}
    </a>
  );
}

function ConnectorIcon({ name }: { name: ConnectorIconName }) {
  const className = `voice-cao-connector-icon is-${name}`;
  if (name === "consul")
    return <ComposerConnectorMark kind="consul" className={className} />;
  if (name === "arbitrum")
    return <ComposerConnectorMark kind="arbitrum" className={className} />;
  if (name === "riskflow")
    return <RadioTower className={className} aria-hidden="true" />;
  return <MessageCircle className={className} aria-hidden="true" />;
}

const riskFlowNotification: RiskFlowNotification = {
  id: "riskflow-sandbox-popup",
  type: "iv",
  category: "riskflow",
  severity: "high",
  title: "Fintheon · RiskFlow",
  message: "Treasury auction tail lifts duration risk into the close.",
  timestamp: new Date(Date.now() - 4 * 60 * 1000),
  eventId: "riskflow-sandbox-headline",
  ivScore: 8.4,
  riskType: "Liquidity",
  source: "financial-juice",
  metadata: {
    headlineIvScore: 8.4,
    headlineEventWeight: 8.7,
    headlineFreshness: 0.92,
    headlineImpactWeight: 7.9,
    pointRange: 46.25,
    direction: "Bearish",
    instrument: "/NQ",
    subScores: {
      eventWeight: 8.7,
      timing: 1.4,
      deviation: 0.8,
      momentum: 1.6,
      vixContext: 5.5,
      vixMultiplier: 1.08,
    },
  },
};
